import React, { useEffect, useMemo, useRef, useState, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import * as XLSX from "xlsx";
import api, { formatApiErrorDetail } from "../lib/api";
import { computeGroups } from "../lib/grouping";
import { assignPosColors, colorMode } from "../lib/posColors";
import { cellValue } from "../lib/cellValue";
import { computeUnits } from "../lib/bubbleLayout";
import { renderPdfPages } from "../lib/pdfRender";
import { downloadBytes } from "../lib/pdfExport";
import { ExportPdfDialog } from "../components/ExportPdfDialog";
import { ProjectInfoDialog } from "../components/ProjectInfoDialog";
import { reviewToRows, AI_FIELDS } from "../lib/aiReview";
import { AiHistoryDialog } from "../components/AiHistoryDialog";
import PdfViewer from "../components/PdfViewer";
import OrderTable from "../components/OrderTable";
import SummaryPanel from "../components/SummaryPanel";
import { ProjectName } from "../components/ProjectName";
import { AiAnalyzeDialog } from "../components/AiAnalyzeDialog";
import { drawingTypeOf } from "../lib/aiLearning";
import { useAiReview } from "../lib/useAiReview";
import { useAiCustomFields, mergeScan, knownExamples, nm } from "../lib/aiCustom";
import { AiMoveConfirm } from "../components/AiMoveConfirm";
import { AiPromptDialog } from "../components/AiPromptDialog";
import { Button } from "../components/ui/button";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../components/ui/tabs";
import { ResizablePanelGroup, ResizablePanel, ResizableHandle } from "../components/ui/resizable";
import { toast } from "sonner";
import { useI18n } from "../context/LanguageContext";
import { tr } from "../i18n";
import { LanguageSelector } from "../components/LanguageSelector";
import {
    ArrowLeft, Download, Upload as UploadIcon, FileDown, Loader2,
    CheckCircle2, Table as TableIcon, LayoutList, Ruler, Undo2, Redo2, X, Share2, MessageSquare, ChevronDown, Eye, Files, Info,
} from "lucide-react";
import { BinderDialog } from "../components/BinderDialog";
import { useAuth } from "../context/AuthContext";
import { useComments } from "../lib/useComments";
import { history, useHistoryLabels } from "../lib/history";
import { describeChange } from "../lib/historyLabels";
import { CommentsPanel } from "../components/comments/CommentsPanel";
import { ShareDialog } from "../components/ShareDialog";
import { buildExportPdf } from "../lib/commentExport";
import { fetchCommentImage } from "../lib/useImageUrl";
import { DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem } from "../components/ui/dropdown-menu";

let idCounter = 0;
const uid = () => `${Date.now().toString(36)}-${(idCounter++).toString(36)}-${Math.random().toString(36).slice(2, 6)}`;
const DEFAULT_TRAY = { page: 1, x: 0.04, y: 0.04, visible: true, paused: false, opaque: false, bubble_scale: 1, bubble_shape: "circle", bubble_color: null, color: "#2563eb" };
const DEFAULT_COLUMNS = [
    { key: "position", label: "Post.", type: "text", width: 70 },
    { key: "qty", label: "Ant.", type: "number", width: 70 },
    { key: "blindW", label: "Žaliuzės plotis", type: "calc", calc: "blindW", width: 120 },
    { key: "blindH", label: "Žaliuzės aukštis", type: "calc", calc: "blindH", width: 120 },
    { key: "motor", label: "Motor", type: "text", width: 110, options: "motor" },
    { key: "type", label: "Side", type: "text", width: 120, options: "side" },
    { key: "color", label: "Kabel type", type: "text", width: 110, options: "kabel" },
    { key: "notes", label: "Infesting", type: "text", width: 101, options: "infesting" },
    { key: "borring", label: "Borring Styr", type: "text", width: 148, custom: true, options: "borring" },
    { key: "width", label: "Bredde", type: "number", width: 90 },
    { key: "height", label: "Høyde", type: "number", width: 90 },
    { key: "blindPos", label: "Pos.", type: "text", width: 65 },
    { key: "leng_al", label: "Leng. Al skims", type: "text", width: 149, custom: true },
    { key: "area", label: "m²", type: "calc", calc: "area", width: 80, custom: true },
    { key: "price", label: tr("col_price"), type: "number", width: 90, custom: true },
];

function nextPosition(rows) {
    let max = 0;
    rows.forEach((r) => {
        const m = /P?(\d+)/i.exec(String(r.position || ""));
        if (m) max = Math.max(max, parseInt(m[1], 10));
    });
    return `P${max + 1}`;
}

const EMPTY_SKIP = new Set(["id", "blindPos", "qty", "position", "parked"]);
const isEmptyRow = (r) => Object.entries(r).every(([k, v]) => EMPTY_SKIP.has(k) || v == null || v === "" || v === false);
const emptyRow = () => ({ id: uid(), position: "", width: null, height: null, type: "", motor: "", color: "", qty: 1, notes: "" });
const START_ROWS = 30;

// Put imported rows into empty rows first, append the rest.
function fillEmptyRows(prev, incoming) {
    const queue = [...incoming];
    const out = prev.map((r) => (queue.length && isEmptyRow(r) ? queue.shift() : r));
    return [...out, ...queue];
}

// Assign blind positions to filled rows missing one. Prefix follows the nearest
// numbered row above (P1 -> P2, C1 -> C2) and continues that prefix's highest number.
const POS_RE = /^(.*?)(\d+)$/;
function fillBlindPos(rows) {
    const maxBy = {};
    rows.forEach((r) => {
        const m = POS_RE.exec(String(r.blindPos || "").trim());
        if (m) maxBy[m[1]] = Math.max(maxBy[m[1]] || 0, parseInt(m[2], 10));
    });
    let prefix = "P";
    return rows.map((r) => {
        if (r.blindPos) {
            const m = POS_RE.exec(String(r.blindPos).trim());
            if (m) prefix = m[1];
            return r;
        }
        if (isEmptyRow(r)) return r;
        const n = (maxBy[prefix] || 0) + 1;
        maxBy[prefix] = n;
        return { ...r, blindPos: `${prefix}${n}` };
    });
}

export default function Workspace() {
    const { id } = useParams();
    const { t, lang } = useI18n();
    const { user } = useAuth();
    const [selectedCommentId, setSelectedCommentId] = useState(null);
    const [shareOpen, setShareOpen] = useState(false);
    const [binderOpen, setBinderOpen] = useState(false);
    const [exportOpen, setExportOpen] = useState(false);
    const [infoOpen, setInfoOpen] = useState(false);
    const [aiAnalysisId, setAiAnalysisId] = useState(null);
    const [historyOpen, setHistoryOpen] = useState(false);
    const prevRowsRef = useRef(null);
    const navigate = useNavigate();
    const [project, setProject] = useState(null);
    const [rows, setRows] = useState([]);
    const [tray, setTray] = useState(DEFAULT_TRAY);
    const [unitOffsets, setUnitOffsets] = useState({});
    const [bubbleStyles, setBubbleStyles] = useState({});
    const [activeRowId, setActiveRowId] = useState(null);
    const [jump, setJump] = useState(null);
    const [locate, setLocate] = useState(null);
    const [flashIds, setFlashIds] = useState([]);
    const [pageNames, setPageNames] = useState({});
    const [columns, setColumns] = useState(DEFAULT_COLUMNS);
    const [blindOffsets, setBlindOffsets] = useState({ width: 0, height: 0 });
    const [activity, setActivity] = useState([]);
    const [search, setSearch] = useState("");
    const [typeFilter, setTypeFilter] = useState("__all__");
    const [pdfData, setPdfData] = useState(null);
    const [uploading, setUploading] = useState(false);
    const [exporting, setExporting] = useState(false);
    const [saveState, setSaveState] = useState("saved");
    const [ready, setReady] = useState(false);
    const [tab, setTab] = useState("table");
    const readOnly = project?.my_role === "commenter";
    const cm = useComments(id, user, project?.my_role);
    const hl = useHistoryLabels();
    const dateFmt = useCallback((iso) => {
        try { return new Date(iso).toLocaleString({ lt: "lt-LT", no: "nb-NO" }[lang] || "en-GB", { dateStyle: "short", timeStyle: "short" }); } catch { return iso; }
    }, [lang]);
    const focusComment = (c) => { setSelectedCommentId(c.id); setJump({ page: c.page, n: Date.now() }); };
    const replyComment = (c) => { setSelectedCommentId(c.id); setTab("comments"); };
    const [aiOpen, setAiOpen] = useState(false);
    const [aiPromptOpen, setAiPromptOpen] = useState(false);
    const [aiFocus, setAiFocus] = useState(null);
    const [aiSuggestions, setAiSuggestions] = useState([]);
    const [aiDrawingType, setAiDrawingType] = useState(null);
    const [aiMarking, setAiMarking] = useState(false);
    const aiReview = useAiReview(aiSuggestions, id, aiDrawingType, t);
    const aiCustom = useAiCustomFields();
    const [aiProgress, setAiProgress] = useState({ running: false, done: 0, total: 0, etaMs: 0, phase: "render" });
    const [visibleRowIds, setVisibleRowIds] = useState(null);
    const fileInputRef = useRef(null);
    const aiCancelRef = useRef(false);
    const aiAbortRef = useRef(null);

    const rowColors = useMemo(() => {
        const { rowColors: g } = computeGroups(rows);
        const uniform = colorMode(tray) === "uniform";
        const label = (r) => (isEmptyRow(r) ? null : r.blindPos || r.position);
        const map = uniform ? {} : assignPosColors(rows.map(label), tray.color_assign === "manual" ? (tray.pos_colors || {}) : {});
        return Object.fromEntries(rows.map((r) => {
            const hex = !label(r) ? null : uniform ? (tray.bubble_color || "#2563eb") : map[label(r)];
            return [r.id, hex ? { ...(g[r.id] || {}), hex } : g[r.id]];
        }));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [rows, tray.color_mode, tray.color_assign, tray.pos_colors, tray.bubble_color]);

    const addFlash = (ids) => setFlashIds((f) => [...f, ...ids.filter((x) => !f.includes(x))]);
    const goToUnit = (page, rowId) => { setActiveRowId(rowId); addFlash([rowId]); setLocate({ rowId, page, n: Date.now() }); setTray((t) => (t.paused ? { ...t, paused: false } : t)); };
    // Deck = bubble position label ("Žaliuzės poz.", e.g. V06 / A-P6): highlight every row of that deck and blink their bubbles.
    const goToDeck = (deck, rowId) => {
        const key = deck.trim().toLowerCase();
        const ids = rows.filter((r) => String(r.blindPos || "").split(/[,;]+/).some((s) => s.trim().toLowerCase() === key)).map((r) => r.id);
        const target = ids.includes(rowId) ? rowId : ids[0] || rowId;
        setActiveRowId(target);
        addFlash(ids.length ? ids : [rowId]);
        setLocate({ rowId: target, n: Date.now() });
        setTray((t) => (t.paused ? { ...t, paused: false } : t));
    };
    const locateRow = (rowId, full) => {
        const on = flashIds.includes(rowId);
        if (on && !full) { setFlashIds((f) => f.filter((x) => x !== rowId)); return; }
        if (!on) setFlashIds((f) => [...f, rowId]);
        setLocate({ rowId, full, n: Date.now() });
        setTray((t) => (t.paused ? { ...t, paused: false } : t));
    };
    const dropFlash = (rowId) => setFlashIds((f) => f.filter((x) => x !== rowId));
    useEffect(() => {
        setFlashIds((f) => (f.every((id) => rows.some((r) => r.id === id)) ? f : f.filter((id) => rows.some((r) => r.id === id))));
    }, [rows]);

    // Load project
    useEffect(() => {
        (async () => {
            try {
                const { data } = await api.get(`/projects/${id}`);
                setProject(data);
                setRows(data.rows && data.rows.length
                    ? data.rows.map((r) => (r.blindPos && isEmptyRow(r) ? { ...r, blindPos: "" } : r))
                    : Array.from({ length: START_ROWS }, emptyRow));
                setTray(data.tray || DEFAULT_TRAY);
                setUnitOffsets(data.unit_offsets || {});
                setBubbleStyles(data.bubble_styles || {});
                setPageNames(data.page_names || {});
                const loadedCols = data.columns && data.columns.length ? data.columns : DEFAULT_COLUMNS;
                let migratedCols = loadedCols.some((c) => c.key === "blindPos")
                    ? loadedCols
                    : [loadedCols[0], { key: "blindPos", label: tr("col_blind_pos"), type: "text", width: 90 }, ...loadedCols.slice(1)];
                if (!migratedCols.some((c) => c.key === "blindW"))
                    migratedCols = [...migratedCols, { key: "blindW", label: "Žaliuzės plotis", type: "calc", calc: "blindW", width: 120 }];
                if (!migratedCols.some((c) => c.key === "blindH"))
                    migratedCols = [...migratedCols, { key: "blindH", label: "Žaliuzės aukštis", type: "calc", calc: "blindH", width: 120 }];
                if (!migratedCols.some((c) => c.key === "area")) migratedCols = [...migratedCols, DEFAULT_COLUMNS.find((c) => c.key === "area")];
                if (!migratedCols.some((c) => c.key === "price")) migratedCols = [...migratedCols, DEFAULT_COLUMNS.find((c) => c.key === "price")];
                setColumns(migratedCols);
                setBlindOffsets(data.blind_offsets || { width: 0, height: 0 });
                setActivity(data.activity || []);
                if (data.pdf_path) {
                    const r = await api.get(`/projects/${id}/pdf`, { responseType: "arraybuffer" });
                    setPdfData(new Uint8Array(r.data));
                }
                setReady(true);
            } catch (e) {
                toast.error(formatApiErrorDetail(e.response?.data?.detail) || tr("msg_load_project_failed"));
                navigate("/dashboard");
            }
        })();
    }, [id, navigate]);

    // ---- Global Undo / Redo history (one stack for table, PDF, comments, AI and Binder; see lib/history.js) ----
    const lastSnapRef = useRef(null);
    const lastObjRef = useRef(null);
    const curEntryRef = useRef(null);
    const applyingRef = useRef(false);
    const interactingRef = useRef(false);
    const pendingPushRef = useRef(false);
    const mergeNextRef = useRef(false);
    useEffect(() => { history.clear(); return () => history.clear(); }, [id]);

    // Group a drag or a single cell-editing session into one undo step.
    const beginInteraction = useCallback(() => { interactingRef.current = true; pendingPushRef.current = true; }, []);
    const endInteraction = useCallback(() => { interactingRef.current = false; pendingPushRef.current = false; }, []);

    const aiItems = aiReview.items;
    useEffect(() => {
        if (!ready) return;
        const obj = { rows, tray, unitOffsets, pageNames, columns, bubbleStyles, ai: aiItems };
        const cur = JSON.stringify(obj);
        const syncOnly = applyingRef.current || mergeNextRef.current || lastSnapRef.current == null;
        if (syncOnly) {
            if (mergeNextRef.current && !applyingRef.current && curEntryRef.current) curEntryRef.current.after = obj;
            applyingRef.current = false; mergeNextRef.current = false;
            lastSnapRef.current = cur; lastObjRef.current = obj;
            return;
        }
        if (lastSnapRef.current === cur) return;
        if (interactingRef.current && !pendingPushRef.current && curEntryRef.current) {
            curEntryRef.current.after = obj;
        } else {
            const e = { label: describeChange(lastObjRef.current, obj, t), before: lastObjRef.current, after: obj };
            e.undo = () => applySnap(e.before);
            e.redo = () => applySnap(e.after);
            if (history.push(e)) curEntryRef.current = e;
            pendingPushRef.current = false;
        }
        lastSnapRef.current = cur; lastObjRef.current = obj;
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [rows, tray, unitOffsets, pageNames, columns, ready, bubbleStyles, aiItems]);

    // Stamp who/when changed a field of an AI-imported row (merged into the same undo step).
    useEffect(() => {
        const prev = prevRowsRef.current;
        prevRowsRef.current = rows;
        if (!ready || !prev || prev === rows) return;
        const byId = new Map(prev.map((r) => [r.id, r]));
        const who = user?.name || user?.email || "";
        let changed = false;
        const next = rows.map((r) => {
            const o = byId.get(r.id);
            if (!r.ai_orig || !o || o === r) return r;
            let edits = r.edits;
            Object.keys(r.ai_orig).forEach((k) => {
                if (String(o[k] ?? "") === String(r[k] ?? "")) return;
                edits = { ...(edits || {}) };
                if (String(r.ai_orig[k] ?? "") === String(r[k] ?? "")) delete edits[k];
                else edits[k] = { by: who, at: new Date().toISOString() };
            });
            if (edits === r.edits) return r;
            changed = true;
            return { ...r, edits };
        });
        if (changed) { mergeNextRef.current = true; prevRowsRef.current = next; setRows(next); }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [rows, ready]);

    // Auto-number the "Post." column 1..N top to bottom (merged into the same undo step).
    useEffect(() => {
        if (!ready || !rows.some((r, i) => r.position !== String(i + 1))) return;
        mergeNextRef.current = true;
        setRows((prev) => prev.map((r, i) => (r.position === String(i + 1) ? r : { ...r, position: String(i + 1) })));
    }, [rows, ready]);

    // Auto-number the "Žaliuzės poz." column (P1, P2, P3...) for any rows missing it.
    // Covers all add paths (load, manual add, AI import, Excel import). Bubbles use blindPos.
    useEffect(() => {
        if (!ready) return;
        if (rows.some((r) => !r.blindPos && !isEmptyRow(r))) { mergeNextRef.current = true; setRows((prev) => fillBlindPos(prev)); }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [rows, ready]);

    // Clean up offsets for units that no longer exist (row deleted / qty reduced)
    useEffect(() => {
        if (!ready) return;
        const valid = new Set(computeUnits(rows, rowColors).map((u) => u.unitId));
        setUnitOffsets((prev) => {
            const next = {};
            let changed = false;
            for (const k of Object.keys(prev)) {
                if (valid.has(k)) next[k] = prev[k]; else changed = true;
            }
            if (changed) mergeNextRef.current = true;
            return changed ? next : prev;
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [rows, ready]);

    // Autosave (debounced)
    const saveTimer = useRef(null);
    useEffect(() => {
        if (!ready || readOnly) return;
        setSaveState("saving");
        if (saveTimer.current) clearTimeout(saveTimer.current);
        saveTimer.current = setTimeout(async () => {
            try {
                await api.put(`/projects/${id}`, { rows, tray, unit_offsets: unitOffsets, page_names: pageNames, columns, bubble_styles: bubbleStyles, blind_offsets: blindOffsets, activity });
                setSaveState("saved");
            } catch {
                setSaveState("saved");
                toast.error(tr("msg_save_failed"));
            }
        }, 700);
        return () => saveTimer.current && clearTimeout(saveTimer.current);
    }, [rows, tray, unitOffsets, pageNames, columns, ready, id, bubbleStyles, blindOffsets, activity, readOnly]);

    const breakStep = useCallback(() => {
        if (interactingRef.current) pendingPushRef.current = true;
    }, []);

    useEffect(() => {
        const isCell = (el) => el?.dataset?.cell && el.closest?.('[data-testid="order-table"]');
        const onIn = (e) => { if (isCell(e.target)) beginInteraction(); };
        const onOut = (e) => { if (isCell(e.target)) endInteraction(); };
        document.addEventListener("focusin", onIn);
        document.addEventListener("focusout", onOut);
        return () => { document.removeEventListener("focusin", onIn); document.removeEventListener("focusout", onOut); };
    }, [beginInteraction, endInteraction]);

    const applySnap = (s) => {
        if (JSON.stringify(s) === lastSnapRef.current) return;
        applyingRef.current = true;
        setRows(s.rows);
        setTray(s.tray);
        setUnitOffsets(s.unitOffsets);
        setPageNames(s.pageNames);
        setBubbleStyles(s.bubbleStyles || {});
        if (s.columns) setColumns(s.columns);
        aiReview.setItems(s.ai || []);
    };

    const queueRef = useRef(Promise.resolve());
    const undo = useCallback(() => { queueRef.current = queueRef.current.then(async () => {
        const e = await history.undo().catch(() => { toast.error(t("h_failed")); return false; });
        if (e === false) return;
        if (!e) { toast.info(t("msg_nothing_undo")); return; }
        breakStep();
        toast.message(t("h_undone", { label: e.label }));
        await new Promise((r) => setTimeout(r, 0));
    }); }, [breakStep, t]);

    const redo = useCallback(() => { queueRef.current = queueRef.current.then(async () => {
        const e = await history.redo().catch(() => { toast.error(t("h_failed")); return false; });
        if (e === false) return;
        if (!e) { toast.info(t("msg_nothing_redo")); return; }
        breakStep();
        toast.message(t("h_redone", { label: e.label }));
        await new Promise((r) => setTimeout(r, 0));
    }); }, [breakStep, t]);

    useEffect(() => {
        const onKey = (e) => {
            const k = (e.key || "").toLowerCase();
            if (!(e.ctrlKey || e.metaKey) || (k !== "z" && k !== "y")) return;
            const el = e.target;
            const editable = el && (/^(input|textarea|select)$/i.test(el.tagName) || el.isContentEditable);
            if (editable && !el.closest?.('[data-testid="order-table"]')) return;
            e.preventDefault();
            if (k === "z" && !e.shiftKey) undo(); else redo();
        };
        window.addEventListener("keydown", onKey);
        return () => window.removeEventListener("keydown", onKey);
    }, [undo, redo]);

    // --- Row ops ---
    const makeRow = (list) => ({ id: uid(), position: nextPosition(list), width: null, height: null, type: "", motor: "", color: "", qty: 1, notes: "" });
    const addRow = () => {
        const r = makeRow(rows);
        setRows((p) => [...p, r]);
        setActiveRowId(r.id);
    };
    const copyRow = (rid) => {
        const src = rows.find((r) => r.id === rid);
        if (!src) return;
        const copy = { ...src, id: uid(), position: nextPosition(rows) };
        const idx = rows.findIndex((r) => r.id === rid);
        setRows((p) => [...p.slice(0, idx + 1), copy, ...p.slice(idx + 1)]);
        setActiveRowId(copy.id);
    };
    const deleteRow = (rid) => {
        setRows((p) => p.filter((r) => r.id !== rid));
        if (activeRowId === rid) setActiveRowId(null);
    };

    // Merge all rows of a duplicate group into one chosen position.
    const mergePositions = (targetRowId, memberRowIds) => {
        const target = rows.find((r) => r.id === targetRowId);
        if (!target || memberRowIds.length < 2) return;
        const members = rows.filter((r) => memberRowIds.includes(r.id));
        const totalQty = members.reduce((a, r) => a + Math.max(1, parseInt(r.qty, 10) || 1), 0);
        // Preserve placed bubbles: collect their offsets in order, reassign to the merged row.
        const collected = [];
        members.forEach((r) => {
            const n = Math.max(1, parseInt(r.qty, 10) || 1);
            for (let i = 0; i < n; i++) {
                const off = unitOffsets[`${r.id}:${i}`];
                if (off) collected.push(off);
            }
        });
        const newOffsets = { ...unitOffsets };
        members.forEach((r) => {
            const n = Math.max(1, parseInt(r.qty, 10) || 1);
            for (let i = 0; i < n; i++) delete newOffsets[`${r.id}:${i}`];
        });
        collected.forEach((off, idx) => { newOffsets[`${targetRowId}:${idx}`] = off; });
        const newRows = rows
            .filter((r) => r.id === targetRowId || !memberRowIds.includes(r.id))
            .map((r) => (r.id === targetRowId ? { ...r, qty: totalQty } : r));
        setRows(newRows);
        setUnitOffsets(newOffsets);
        setActiveRowId(targetRowId);
        const primaryLabel = target.blindPos || target.position || "—";
        const mergedLabels = members.filter((r) => r.id !== targetRowId).map((r) => r.blindPos || r.position || "—").join(", ");
        setActivity((prev) => [...prev, {
            id: `log_${Date.now()}`,
            ts: new Date().toISOString(),
            type: "merge",
            text: t("msg_merged_log", { a: mergedLabels, b: primaryLabel, n: totalQty }),
        }]);
        toast.success(t("msg_merged", { b: primaryLabel, n: totalQty }));
    };

    // Renumber blindPos sequentially (P1, P2, P3...) by current row order, without
    // touching any other data (widths, drawings, bubbles, PDF stay intact).
    const renumberBlindPos = () => {
        setRows((prev) => {
            const counters = {};
            return prev.map((r) => {
                if (isEmptyRow(r)) return r;
                const m = POS_RE.exec(String(r.blindPos || "").trim());
                if (!m) return r;
                counters[m[1]] = (counters[m[1]] || 0) + 1;
                return { ...r, blindPos: `${m[1]}${counters[m[1]]}` };
            });
        });
        setActivity((prev) => [...prev, {
            id: `log_${Date.now()}`,
            ts: new Date().toISOString(),
            type: "renumber",
            text: t("msg_renumber_log"),
        }]);
        toast.success(t("sum_renumber_done"));
    };

    // --- AI drawing analysis (multi-page, per-field detection) ---
    const analyzeWithAi = async (opts = {}) => {
        if (!pdfData) { toast.info(t("ai_no_pdf")); return; }
        aiCancelRef.current = false;
        setAiProgress({ running: true, done: 0, total: 0, etaMs: 0, phase: "render" });
        try {
            const pages = await renderPdfPages(pdfData.slice(0), { scale: 1.25 });
            if (aiCancelRef.current) { toast.info(t("ai_cancelled")); return; }
            setAiProgress({ running: true, done: 0, total: pages.length, etaMs: 0, phase: "detect" });
            const all = [];
            let ctx = null;
            const t0 = Date.now();
            for (let i = 0; i < pages.length; i++) {
                if (aiCancelRef.current) break;
                const pg = pages[i];
                try {
                    const controller = new AbortController();
                    aiAbortRef.current = controller;
                    const { data } = await api.post(`/projects/${id}/ai-detect`, { image_base64: pg.dataUrl, page: pg.page, drawing_type: drawingTypeOf(pg.w, pg.h), context: ctx, ...(opts.focus ? { focus_fields: opts.focus, task: opts.task } : {}) }, { signal: controller.signal });
                    (data.items || []).forEach((it) => all.push({ ...it, page: pg.page }));
                    const found = data.items || [];
                    if (found.length) ctx = { page: pg.page, block: [...found].reverse().find((x) => x.block)?.block || "", names: found.slice(-12).map((x) => x.name?.value).filter(Boolean),
                        incomplete: found.slice(-3).filter((x) => x.name?.value && (x.width?.value == null || x.height?.value == null)).map((x) => x.name.value) };
                } catch { /* skip failing / aborted page */ }
                aiAbortRef.current = null;
                if (aiCancelRef.current) break;
                const done = i + 1;
                const avg = (Date.now() - t0) / done;
                setAiProgress({ running: true, done, total: pages.length, etaMs: Math.max(0, avg * (pages.length - done)), phase: "detect" });
            }
            if (aiCancelRef.current) { toast.info(t("ai_cancelled")); return; }
            if (all.length === 0) { toast.info(t("ai_none")); return; }
            let linked = all;
            setAiProgress((p) => ({ ...p, phase: "link" }));
            try {
                const { data } = await api.post(`/projects/${id}/ai-link`, { items: all, drawing_type: drawingTypeOf(pages[0]?.w, pages[0]?.h) });
                if (Array.isArray(data.items) && data.items.length) linked = data.items;
            } catch { /* keep per-page result */ }
            try {
                const { data: saved } = await api.post(`/projects/${id}/ai-analyses`, { items: linked, pages: pages.length });
                setAiAnalysisId(saved.id);
            } catch { setAiAnalysisId(null); }
            setAiDrawingType(drawingTypeOf(pages[0]?.w, pages[0]?.h));
            setAiSuggestions(linked);
            setAiOpen(true);
        } catch (e) {
            if (!aiCancelRef.current) toast.error(formatApiErrorDetail(e.response?.data?.detail) || t("ai_error"));
        } finally {
            setAiProgress((p) => ({ ...p, running: false }));
        }
    };

    // Natural-language task: create missing columns, then analyse with the chosen fields only (other columns hidden).
    const runAiTask = async ({ fields, task, full }) => {
        const keys = [];
        for (const f of fields) {
            if (f.key) keys.push(f.key);
            else { try { keys.push((await aiCustom.create(f.label)).key); } catch { /* skip */ } }
        }
        setAiFocus(full ? { all: true } : keys);
        await analyzeWithAi(full ? {} : { focus: keys.map((k) => `${k} (${t(`ai_f_${k}`)})`), task });
    };

    // "Rescan": whole PDF (every page, table, specification, description) searched again only for the user-defined columns.
    const rescanCustom = async (keys) => {
        if (!pdfData) { toast.info(t("ai_no_pdf")); return; }
        if (!keys.length) return;
        aiCancelRef.current = false;
        setAiProgress({ running: true, done: 0, total: 0, etaMs: 0, phase: "render" });
        try {
            const pages = await renderPdfPages(pdfData.slice(0), { scale: 1.25 });
            const base = aiReview.items;
            const positions = [...new Set(base.map((it) => String(it.f.name?.value ?? "").trim()).filter(Boolean))];
            const known = knownExamples(base, keys);
            const found = [];
            const t0 = Date.now();
            setAiProgress({ running: true, done: 0, total: pages.length, etaMs: 0, phase: "custom" });
            for (let i = 0; i < pages.length; i++) {
                if (aiCancelRef.current) break;
                const pg = pages[i];
                try {
                    const controller = new AbortController();
                    aiAbortRef.current = controller;
                    const { data } = await api.post(`/projects/${id}/ai-custom-scan`, { image_base64: pg.dataUrl, page: pg.page, keys, positions, known }, { signal: controller.signal });
                    (data.items || []).forEach((x) => found.push({ ...x, page: pg.page }));
                } catch { /* skip failing / aborted page */ }
                aiAbortRef.current = null;
                const done = i + 1;
                setAiProgress({ running: true, done, total: pages.length, etaMs: Math.max(0, ((Date.now() - t0) / done) * (pages.length - done)), phase: "custom" });
            }
            if (aiCancelRef.current) { toast.info(t("ai_cancelled")); return; }
            const res = mergeScan(aiReview.items, found, keys);
            aiReview.setItems(res.items);
            toast.success(t("cf_scan_done", { n: res.filled, pages: pages.length }));
        } catch {
            if (!aiCancelRef.current) toast.error(t("ai_error"));
        } finally {
            setAiProgress((p) => ({ ...p, running: false }));
        }
    };

    // "Recalculate position sequence": AI re-reads the pages of the positions from the corrected one onward, using the user's fixes.
    const resequenceAi = async () => {
        const ch = aiReview.chain;
        if (!ch || !pdfData) return;
        const list = aiReview.items;
        const affected = list.slice(Math.max(0, list.findIndex((x) => x._id === ch.startId)));
        const pagesOf = (it) => (it.pages?.length ? it.pages : [it.page]);
        const pages = [...new Set(affected.flatMap(pagesOf))].sort((a, b) => a - b);
        const keys = AI_FIELDS.map((f) => f.key).filter((k) => k !== "name");
        aiCancelRef.current = false;
        setAiProgress({ running: true, done: 0, total: pages.length, etaMs: 0, phase: "reseq" });
        try {
            const imgs = await renderPdfPages(pdfData.slice(0), { scale: 1.25, only: pages });
            const found = [];
            for (let i = 0; i < imgs.length; i++) {
                if (aiCancelRef.current) break;
                const pg = imgs[i];
                const rowsOnPage = affected.filter((it) => pagesOf(it).includes(pg.page)).map((it) => ({ name: it.f.name?.value ?? "", ...Object.fromEntries(keys.map((k) => [k, it.f[k]?.value ?? ""])) }));
                try {
                    const controller = new AbortController();
                    aiAbortRef.current = controller;
                    const { data } = await api.post(`/projects/${id}/ai-resequence`, { image_base64: pg.dataUrl, page: pg.page, items: rowsOnPage, fixes: ch.fixes }, { signal: controller.signal });
                    (data.items || []).forEach((x) => found.push({ ...x, page: pg.page }));
                } catch { /* skip page */ }
                aiAbortRef.current = null;
                setAiProgress({ running: true, done: i + 1, total: imgs.length, etaMs: 0, phase: "reseq" });
            }
            if (aiCancelRef.current) { toast.info(t("ai_cancelled")); return; }
            let changed = 0;
            const ids = new Set(affected.map((x) => x._id));
            const next = aiReview.items.map((it) => {
                if (!ids.has(it._id)) return it;
                const hits = found.filter((x) => nm(x.name?.value) === nm(it.f.name?.value) && nm(it.f.name?.value));
                if (!hits.length) return it;
                const f = { ...it.f };
                keys.forEach((k) => {
                    const cur = f[k] || {};
                    if (cur.edited || cur.manual || cur.fb === "ok") return;
                    const h = hits.find((x) => x[k] && String(x[k].value ?? "").trim() !== "");
                    if (!h) return;
                    const nv = h[k].value ?? "";
                    if (String(nv).trim() === String(cur.value ?? "").trim()) return;
                    f[k] = { ...cur, value: nv, box: h[k].box || null, conf: h[k].conf ?? 0.85, page: h.page !== it.page ? h.page : undefined, reseq: true, learned: null };
                    changed += 1;
                });
                return { ...it, f };
            });
            aiReview.setItems(next);
            aiReview.setChain(null);
            toast.success(t("mv_reseq_done", { n: changed }));
        } catch {
            if (!aiCancelRef.current) toast.error(t("ai_error"));
        } finally {
            setAiProgress((p) => ({ ...p, running: false }));
        }
    };

    const cancelAi = () => {
        aiCancelRef.current = true;
        try { aiAbortRef.current?.abort(); } catch { /**/ }
        setAiProgress((p) => ({ ...p, running: false }));
    };

    const clearAiColoring = () => {
        setRows((prev) => prev.map((r) => {
            if (!r.ai) return r;
            // eslint-disable-next-line no-unused-vars
            const { ai, ...rest } = r;
            return rest;
        }));
        toast.success(t("ai_cleared"));
    };
    const importAiRows = (items, importCols) => {
        const { rows: newRows, newCols } = reviewToRows(items, importCols, uid, columns, aiAnalysisId);
        if (newCols.length) setColumns((cs) => [...cs, ...newCols]);
        setRows((p) => fillEmptyRows(p, newRows));
        if (aiAnalysisId) api.post(`/projects/${id}/ai-analyses/${aiAnalysisId}/imported`, { links: newRows.map((r) => ({ row_id: r.id, idx: r.ai_src.idx })) }).catch(() => {});
        toast.success(t("ai_added", { n: newRows.length }));
    };
    const openArchivedReview = (analysis) => { setAiAnalysisId(analysis.id); setAiSuggestions(analysis.items); setAiOpen(true); };

    // --- PDF upload ---
    const uploadPdf = async (file) => {
        setUploading(true);
        try {
            const fd = new FormData();
            fd.append("file", file);
            const { data } = await api.post(`/projects/${id}/pdf`, fd);
            setProject((p) => ({ ...p, pdf_path: data.pdf_path, pdf_filename: data.pdf_filename }));
            const r = await api.get(`/projects/${id}/pdf`, { responseType: "arraybuffer" });
            setPdfData(new Uint8Array(r.data));
            toast.success(t("msg_pdf_uploaded"));
        } catch (e) {
            toast.error(formatApiErrorDetail(e.response?.data?.detail) || t("msg_pdf_upload_failed"));
        } finally {
            setUploading(false);
        }
    };

    // --- Excel ---
    const exportExcel = () => {
        const headerFor = (c) => (c.calc === "blindW" || c.calc === "blindH" ? t(c.calc === "blindW" ? "ot_blind_w" : "ot_blind_h") : c.label);
        const valueFor = (c, r) => cellValue(c, r, blindOffsets) ?? "";
        const visibleCols = columns.filter((c) => !c.hidden);
        const data = rows.filter((r) => !isEmptyRow(r)).map((r) => {
            const o = {};
            visibleCols.forEach((c) => { o[headerFor(c)] = valueFor(c, r); });
            return o;
        });
        const ws = XLSX.utils.json_to_sheet(data, { header: visibleCols.map(headerFor) });
        const wb = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(wb, ws, t("xlsx_sheet"));
        XLSX.writeFile(wb, `${project?.name || "uzsakymas"}.xlsx`);
    };

    const importExcel = async (file) => {
        try {
            const buf = await file.arrayBuffer();
            const wb = XLSX.read(buf, { type: "array" });
            const ws = wb.Sheets[wb.SheetNames[0]];
            const json = XLSX.utils.sheet_to_json(ws, { defval: "" });
            const key = (obj, names) => {
                const keys = Object.keys(obj);
                for (const n of names) {
                    const f = keys.find((k) => k.trim().toLowerCase() === n);
                    if (f) return obj[f];
                }
                return "";
            };
            const imported = json.map((o) => ({
                id: uid(),
                position: String(key(o, ["pozicija", "poz", "poz."]) || ""),
                width: key(o, ["plotis", "width", "w"]) === "" ? null : Number(key(o, ["plotis", "width", "w"])),
                height: key(o, ["aukštis", "aukstis", "height", "h"]) === "" ? null : Number(key(o, ["aukštis", "aukstis", "height", "h"])),
                type: String(key(o, ["tipas", "type", "gaminys"]) || ""),
                motor: String(key(o, ["variklis", "motor"]) || ""),
                color: String(key(o, ["spalva", "color"]) || ""),
                qty: parseInt(key(o, ["kiekis", "qty", "vnt", "kiek"]), 10) || 1,
                notes: String(key(o, ["pastabos", "notes", "komentaras"]) || ""),
            }));
            const normalized = imported.map((r, i) => ({ ...r, position: r.position || `P${rows.filter((x) => !isEmptyRow(x)).length + i + 1}` }));
            setRows((p) => fillEmptyRows(p, normalized));
            toast.success(t("msg_imported_rows", { n: normalized.length }));
        } catch {
            toast.error(t("msg_excel_read_failed"));
        }
    };

    const openExport = () => { if (!pdfData) { toast.info(t("ex_no_pdf")); return; } setExportOpen(true); };
    const exportPdf = async (opts) => {
        setExporting(true);
        try {
            const fetchImage = (name) => fetchCommentImage(id, name).then((r) => r.blob);
            const bytes = await buildExportPdf(pdfData.slice(0), {
                ...opts, comments: cm.comments, units: computeUnits(rows, rowColors), tray, unitOffsets, bubbleStyles, rows, columns,
                projectName: project?.name, author: user?.name || user?.email, t, dateFmt, fetchImage,
            });
            downloadBytes(bytes, `${project?.name || "projektas"}-${opts.content}.pdf`);
            toast.success(t("ex_done"));
            setExportOpen(false);
        } catch (e) {
            console.error(e);
            toast.error(t("ex_failed"));
        } finally {
            setExporting(false);
        }
    };
    const saveBinder = async ({ bytes, mapping, deleted, pageNames: names }) => {
        const prevBytes = pdfData ? pdfData.slice(0) : null;
        const prevComments = cm.comments;
        const fname = project?.pdf_filename || `${project?.name || "binder"}.pdf`;
        history.begin(t("h_binder"));
        try {
            await uploadPdfBytes(bytes, fname);
            setUnitOffsets((o) => {
                const n = {};
                Object.entries(o || {}).forEach(([k, v]) => { if (!deleted.includes(v.page)) n[k] = { ...v, page: mapping[v.page] || v.page }; });
                return n;
            });
            setPageNames(names);
            await api.post(`/projects/${id}/comments/remap`, { mapping, delete_pages: deleted });
            const { data: nextComments } = await api.get(`/projects/${id}/comments`);
            cm.reload();
            if (prevBytes) {
                history.push({
                    label: t("h_binder"),
                    undo: async () => { await uploadPdfBytes(prevBytes, fname); await api.post(`/projects/${id}/comments/replace-all`, { comments: prevComments }); cm.reload(); },
                    redo: async () => { await uploadPdfBytes(bytes, fname); await api.post(`/projects/${id}/comments/replace-all`, { comments: nextComments }); cm.reload(); },
                });
            }
            await new Promise((r) => setTimeout(r, 60));
        } finally { history.end(); }
        toast.success(t("msg_binder_saved"));
    };
    const uploadPdfBytes = async (bytes, fname) => {
        const fd = new FormData();
        fd.append("file", new File([bytes], fname, { type: "application/pdf" }));
        const { data } = await api.post(`/projects/${id}/pdf`, fd);
        setProject((p) => ({ ...p, pdf_path: data.pdf_path, pdf_filename: data.pdf_filename }));
        setPdfData(new Uint8Array(bytes));
    };
    const exportOriginal = () => {
        if (!pdfData) { toast.info(t("msg_no_pdf")); return; }
        downloadBytes(pdfData.slice(0), project?.pdf_filename || `${project?.name || "projektas"}.pdf`);
    };

    if (!project) {
        return <div className="min-h-screen flex items-center justify-center bg-[#0b0f17]"><Loader2 className="w-8 h-8 text-blue-500 animate-spin" /></div>;
    }

    return (
        <div className="h-screen flex flex-col bg-[#0b0f17] text-slate-100 overflow-hidden">
            <header className="h-[57px] shrink-0 bg-[#0b0f17]/95 backdrop-blur-md border-b border-[#1e293b] flex items-center justify-between px-4 gap-3">
                <div className="flex items-center gap-3 min-w-0">
                    <Button data-testid="back-to-dashboard" onClick={() => navigate("/dashboard")} variant="ghost" size="icon"
                        className="h-8 w-8 text-slate-400 hover:bg-[#182232] shrink-0">
                        <ArrowLeft className="w-4 h-4" />
                    </Button>
                    <div className="w-7 h-7 rounded-md bg-blue-600 flex items-center justify-center shrink-0">
                        <Ruler className="w-4 h-4 text-white" />
                    </div>
                    <h1 className="min-w-0 flex">
                        <ProjectName id={id} name={project.name} editable={!readOnly} className="font-mono font-semibold text-base"
                            onRenamed={(n) => setProject((p) => ({ ...p, name: n }))} />
                    </h1>
                    <button data-testid="project-info-button" onClick={() => setInfoOpen(true)} title={t("pj_info")}
                        className="shrink-0 p-1 rounded text-slate-400 hover:text-white hover:bg-[#182232]"><Info className="w-4 h-4" /></button>
                    {project.info?.number && <span data-testid="project-number-badge" className="text-xs font-mono text-blue-300 shrink-0">{project.info.number}</span>}
                    <span className="text-xs font-mono flex items-center gap-1 shrink-0 ml-1">
                        {readOnly
                            ? <span data-testid="commenter-badge" className="flex items-center gap-1 text-sky-300"><Eye className="w-3 h-3" />{t("sh_commenter")}</span>
                            : saveState === "saving"
                            ? <><Loader2 className="w-3 h-3 animate-spin text-amber-400" /><span className="text-amber-400">{t("ws_saving")}</span></>
                            : <><CheckCircle2 className="w-3 h-3 text-emerald-400" /><span className="text-emerald-400">{t("ws_saved")}</span></>}
                    </span>
                </div>
                <div className="flex items-center gap-2 shrink-0">
                    <LanguageSelector />
                    {!readOnly && (
                        <Button data-testid="binder-button" onClick={() => setBinderOpen(true)} variant="outline" size="sm"
                            className="h-8 bg-[#111827] border-[#1e293b] text-slate-200 hover:bg-[#182232]">
                            <Files className="w-4 h-4 mr-1.5" /> Binder
                        </Button>
                    )}
                    {!readOnly && (
                        <Button data-testid="share-project-button" onClick={() => setShareOpen(true)} variant="outline" size="sm"
                            className="h-8 bg-[#111827] border-[#1e293b] text-slate-200 hover:bg-[#182232]">
                            <Share2 className="w-4 h-4 mr-1.5" /> {t("sh_btn")}
                            {(project.shared_emails || []).length > 0 && <span className="ml-1 text-xs text-blue-300">{project.shared_emails.length}</span>}
                        </Button>
                    )}
                    <Button data-testid="undo-button" onClick={undo} variant="ghost" size="sm" disabled={!hl.undo}
                        className="h-8 px-2 text-slate-300 hover:bg-[#182232] disabled:opacity-40" title={hl.undo ? `Undo: ${hl.undo} (Ctrl+Z)` : t("msg_nothing_undo")}>
                        <Undo2 className="w-4 h-4 mr-1" /><span className="hidden xl:inline text-xs">Undo</span>
                    </Button>
                    <Button data-testid="redo-button" onClick={redo} variant="ghost" size="sm" disabled={!hl.redo}
                        className="h-8 px-2 text-slate-300 hover:bg-[#182232] disabled:opacity-40" title={hl.redo ? `Redo: ${hl.redo} (Ctrl+Y)` : t("msg_nothing_redo")}>
                        <Redo2 className="w-4 h-4 mr-1" /><span className="hidden xl:inline text-xs">Redo</span>
                    </Button>
                    <input ref={fileInputRef} type="file" accept=".xlsx,.xls" className="hidden"
                        onChange={(e) => { if (e.target.files[0]) { importExcel(e.target.files[0]); e.target.value = ""; } }} />
                    {!readOnly && <Button data-testid="import-excel-button" onClick={() => fileInputRef.current?.click()} variant="outline" size="sm"
                        className="h-8 bg-[#111827] border-[#1e293b] text-slate-200 hover:bg-[#182232]">
                        <UploadIcon className="w-4 h-4 mr-1.5" /> {t("ws_import")}
                    </Button>}
                    <Button data-testid="export-excel-button" onClick={exportExcel} variant="outline" size="sm"
                        className="h-8 bg-[#111827] border-[#1e293b] text-slate-200 hover:bg-[#182232]">
                        <Download className="w-4 h-4 mr-1.5" /> Excel
                    </Button>
                    <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                            <Button data-testid="pdf-download-menu" disabled={exporting} size="sm" className="h-8 bg-blue-600 hover:bg-blue-500 text-white">
                                {exporting ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : <FileDown className="w-4 h-4 mr-1.5" />} PDF <ChevronDown className="w-3.5 h-3.5 ml-1" />
                            </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end" className="bg-[#111827] border-[#1e293b] text-slate-100">
                            <DropdownMenuItem data-testid="export-pdf-dialog-button" onClick={openExport}>{t("ex_menu")}</DropdownMenuItem>
                            <DropdownMenuItem data-testid="export-original-pdf" onClick={exportOriginal}>{t("pdf_original")}</DropdownMenuItem>
                        </DropdownMenuContent>
                    </DropdownMenu>
                </div>
            </header>

            <div className="flex-1 min-h-0">
                <ResizablePanelGroup direction="horizontal" className="h-full">
                    <ResizablePanel defaultSize={58} minSize={30} className="bg-[#0f172a]">
                        <PdfViewer
                            pdfData={pdfData} hasPdf={!!project.pdf_path} uploading={uploading} onUpload={uploadPdf}
                            rows={rows} rowColors={rowColors}
                            tray={tray} setTray={setTray} unitOffsets={unitOffsets} setUnitOffsets={setUnitOffsets}
                            bubbleStyles={bubbleStyles} setBubbleStyles={setBubbleStyles}
                            activeRowId={activeRowId} setActiveRowId={setActiveRowId} jump={jump} locate={locate} flashIds={flashIds} onLocateMiss={dropFlash}
                            pageNames={pageNames} setPageNames={setPageNames}
                            onInteractionStart={beginInteraction} onInteractionEnd={endInteraction}
                            onAiAnalyze={() => { if (!pdfData) { toast.info(t("ai_no_pdf")); return; } setAiPromptOpen(true); }} onOpenAiHistory={() => setHistoryOpen(true)}
                            aiReview={aiOpen ? aiReview : null} aiMarking={aiMarking} setAiMarking={setAiMarking} projectId={id}
                            onClearAi={clearAiColoring} hasAi={rows.some((r) => r.ai)}
                            onExportPdf={openExport} exporting={exporting}
                            visibleRowIds={visibleRowIds}
                            comments={cm.comments} cm={cm} selectedCommentId={selectedCommentId} setSelectedCommentId={setSelectedCommentId}
                            onReplyComment={replyComment} readOnly={readOnly} dateFmt={dateFmt}
                        />
                    </ResizablePanel>
                    <ResizableHandle className="bg-[#1e293b] w-1.5 hover:bg-blue-600 transition-colors" />
                    <ResizablePanel defaultSize={42} minSize={25} className="bg-[#111827]">
                        <Tabs value={tab} onValueChange={setTab} className="h-full flex flex-col">
                            <TabsList className="shrink-0 m-2 bg-[#0b0f17] border border-[#1e293b]">
                                <TabsTrigger data-testid="tab-table" value="table" className="data-[state=active]:bg-blue-600 data-[state=active]:text-white font-mono text-xs">
                                    <TableIcon className="w-3.5 h-3.5 mr-1.5" /> {t("ws_tab_table")}
                                </TabsTrigger>
                                <TabsTrigger data-testid="tab-summary" value="summary" className="data-[state=active]:bg-blue-600 data-[state=active]:text-white font-mono text-xs">
                                    <LayoutList className="w-3.5 h-3.5 mr-1.5" /> {t("ws_tab_summary")}
                                </TabsTrigger>
                                <TabsTrigger data-testid="tab-comments" value="comments" className="data-[state=active]:bg-blue-600 data-[state=active]:text-white font-mono text-xs">
                                    <MessageSquare className="w-3.5 h-3.5 mr-1.5" /> {t("cm_tab")}
                                    {cm.comments.filter((c) => !c.resolved).length > 0 && <span className="ml-1.5 text-[10px] px-1.5 rounded-full bg-amber-500/20 text-amber-300">{cm.comments.filter((c) => !c.resolved).length}</span>}
                                </TabsTrigger>
                            </TabsList>
                            <TabsContent value="table" className="flex-1 min-h-0 m-0">
                                <fieldset disabled={readOnly} className="h-full min-w-0">
                                <OrderTable
                                    rows={rows} setRows={setRows} rowColors={rowColors} onHistoryBreak={breakStep}
                                    activeRowId={activeRowId} setActiveRowId={setActiveRowId}
                                    search={search} setSearch={setSearch} typeFilter={typeFilter} setTypeFilter={setTypeFilter}
                                    onAddRow={addRow} makeRow={makeRow} onCopyRow={copyRow} onDeleteRow={deleteRow}
                                    unitOffsets={unitOffsets} trayPage={tray.page} onGoToPage={goToUnit} onDeck={goToDeck} onLocate={locateRow} flashIds={flashIds} onClearFlashes={() => setFlashIds([])}
                                    pageNames={pageNames}
                                    paused={!!tray.paused} onTogglePaused={() => setTray((t) => ({ ...t, paused: !t.paused }))}
                                    columns={columns} setColumns={setColumns}
                                    blindOffsets={blindOffsets} setBlindOffsets={setBlindOffsets}
                                    onVisibleChange={setVisibleRowIds}
                                />
                                </fieldset>
                            </TabsContent>
                            <TabsContent value="comments" className="flex-1 min-h-0 m-0">
                                <CommentsPanel comments={cm.comments} cm={cm} selectedId={selectedCommentId} onSelect={focusComment}
                                    dateFmt={dateFmt} t={t} pageNames={pageNames} />
                            </TabsContent>
                            <TabsContent value="summary" className="flex-1 min-h-0 m-0">
                                <SummaryPanel rows={rows} rowColors={rowColors} onMergeGroup={mergePositions} onRenumber={renumberBlindPos} columns={columns} blindOffsets={blindOffsets} />
                            </TabsContent>
                        </Tabs>
                    </ResizablePanel>
                </ResizablePanelGroup>
            </div>
            <ProjectInfoDialog open={infoOpen} onOpenChange={setInfoOpen} projectId={id} info={project.info} editable={!readOnly}
                onSaved={(info) => setProject((p) => ({ ...p, info }))} />
            <ExportPdfDialog open={exportOpen} onOpenChange={setExportOpen} onExport={exportPdf} exporting={exporting} t={t} />
            <AiHistoryDialog open={historyOpen} onOpenChange={setHistoryOpen} projectId={id} rows={rows} setRows={setRows} columns={columns} setColumns={setColumns}
                isEmptyRow={isEmptyRow} uid={uid} fillEmptyRows={fillEmptyRows} onOpenReview={openArchivedReview} onDownloadPdf={exportOriginal} />
            <AiAnalyzeDialog open={aiOpen} onOpenChange={(o) => { setAiOpen(o); if (!o) setAiMarking(false); }} review={aiReview} onImport={importAiRows} marking={aiMarking} setMarking={setAiMarking} onGoPage={(p) => setJump({ page: p, n: Date.now() })}
                custom={aiCustom} onRescan={rescanCustom} onResequence={resequenceAi} focus={aiFocus} />
            <AiPromptDialog open={aiPromptOpen} onOpenChange={setAiPromptOpen} onRun={runAiTask} />
            <AiMoveConfirm review={aiReview} t={t} />
            <ShareDialog open={shareOpen} onOpenChange={setShareOpen} projectId={id} shared={project.shared_emails || []}
                onChange={(list) => setProject((p) => ({ ...p, shared_emails: list }))} t={t} />
            <BinderDialog open={binderOpen} onOpenChange={setBinderOpen} projectId={id} pdfData={pdfData} comments={cm.comments}
                unitOffsets={unitOffsets} pageNames={pageNames} onSaved={saveBinder} />
            {aiProgress.running && (
                <div data-testid="ai-progress-overlay" className="fixed inset-0 z-[60] flex items-center justify-center bg-black/60 backdrop-blur-sm">
                    <div className="bg-[#111827] border border-[#1e293b] rounded-xl px-8 py-6 w-[340px] text-center shadow-2xl">
                        <Loader2 className="w-7 h-7 text-fuchsia-400 animate-spin mx-auto mb-3" />
                        <div data-testid="ai-progress-text" className="text-slate-100 font-mono text-sm">
                            {aiProgress.phase === "render"
                                ? t("ai_rendering")
                                : aiProgress.phase === "link" ? t("ag_linking")
                                : aiProgress.phase === "custom" ? t("cf_scanning", { done: aiProgress.done, total: aiProgress.total })
                                : aiProgress.phase === "reseq" ? t("mv_reseq_progress", { done: Math.min(aiProgress.done + 1, aiProgress.total), total: aiProgress.total })
                                : t("ai_progress", { done: aiProgress.done, total: aiProgress.total })}
                        </div>
                        {(aiProgress.phase === "detect" || aiProgress.phase === "custom") && aiProgress.etaMs > 0 && (
                            <div className="text-slate-400 text-xs mt-1 font-mono">{t("ai_eta", { s: Math.ceil(aiProgress.etaMs / 1000) })}</div>
                        )}
                        <div className="mt-4 h-1.5 bg-[#1e293b] rounded-full overflow-hidden">
                            <div className="h-full bg-gradient-to-r from-fuchsia-500 to-blue-500 transition-all duration-300"
                                style={{ width: aiProgress.total ? `${(aiProgress.done / aiProgress.total) * 100}%` : "15%" }} />
                        </div>
                        <button data-testid="ai-cancel-btn" onClick={cancelAi}
                            className="mt-4 w-full inline-flex items-center justify-center gap-1.5 text-xs font-mono text-slate-300 border border-[#1e293b] rounded-md px-3 py-2 hover:bg-[#182232] hover:text-white transition-colors">
                            <X className="w-3.5 h-3.5" /> {t("ai_cancel")}
                        </button>
                    </div>
                </div>
            )}
        </div>
    );
}
